import IconButton from "components/blocks/IconButton";
import ActionModal from "components/blocks/Modal/ActionModal";
import GridView from "components/GridView";
import { prepareFilters } from "components/GridView/components/GridFilter";
import useGridStore from "components/GridView/grid.store";
import { GridSchema, GridViewHeaderProps } from "components/GridView/GridView.types";
import { useCookieContext } from "contexts";
import { useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import useGridData from "hooks/useGridData";
import { useNotification } from "hooks/useNotification";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FormTemplatesService } from "services/formTemplatesService";
import { PublishingFormLocalResult } from "services/formTemplatesService/models/formTemplatesTypes";
import { useShallow } from "zustand/react/shallow";

const FormTemplatesGridView = () => {
  const { t } = useTranslation("FormTemplates");
  const { setBreadcrumbs } = useBreadcrumb();
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const { showNotification } = useNotification();
  const ready = useRef(false);
  const [data, setData] = useState<PublishingFormLocalResult[]>([]);
  const [modalProps, setModalProps] = useState({
    isOpen: false,
    type: "delete",
    name: "",
    id: 0,
    loading: false,
  });
  const { isAr } = useCookieContext();

  const { loading, gridData, totalCount, exportToExcel } = useGridData<"formTemplatesService", PublishingFormLocalResult>({
    service: "formTemplatesService",
    endpoint: "getAllFormTemplates",
    exportExcelEndpoint: "exportToExcel",
    exportFileName: "formTemplates",
    localizationNameSpace: "FormTemplates",
  });

  const { sortState, pagination, setPagination, searchValue, filters, resetGridStore } = useGridStore(
    useShallow((state) => ({
      sortState: state.sortState,
      pagination: state.pagination,
      setPagination: state.setPagination,
      searchValue: state.generalSearch,
      filters: state.filters,
      resetGridStore: state.reset,
    })),
  );

  useEffect(() => {
    setBreadcrumbs([
      {
        localizationKey: "formTemplates",
        path: "/home/formTemplates",
        menuItemId: "formTemplates",
      },
    ]);
  }, []);

  useEffect(() => {
    if (!ready.current) {
      return;
    }
    fetchData();
  }, [pagination.currentPage]);

  useEffect(() => {
    if (!ready.current) {
      return;
    }
    fetchData({ resetPagination: true });
  }, [sortState, searchValue, filters]);
  
  useEffect(() => {
    if (!ready.current) {
      return;
    }
    fetchData();
  }, [pagination.currentPage]);

  useEffect(() => {
    if (!ready.current) {
      return;
    }
    fetchData({ resetPagination: true });
  }, [sortState, searchValue, filters, isAr]);

  useEffect(() => {
    if (!ready.current) {
      resetGridStore();
      ready.current = true;
      return;
    }
  }, [resetGridStore]);


  const fetchData = async ({ resetPagination = false }: { resetPagination?: boolean } = {})=>
    {
     try
     {
     const response = await FormTemplatesService.getAllFormTemplates
     ({
      pageIndex: resetPagination ? 1 : pagination.currentPage,
      pageSize: 10,
      search: searchValue,
      sort: sortState
      ? Object.keys(sortState).map((key) => ({
          field: key,
          dir: sortState[key],
        }))
      : [],
      filter: [
        ...prepareFilters(filters)
      ],
     });
  
     setData(response.data.result);

     }catch(error)
     {
     }
    }

    const onSwapFormOrder = async ({ from, to }: { from: number; to: number }) => {
      try {
        setModalProps({ ...modalProps, loading: true });

        const currentForm = data[from];
        const targetForm = data[to];
  
        const res = await FormTemplatesService.swapOrder(
          {
            id: currentForm.id,
            order: currentForm.order as number,
          },
          {
            id: targetForm.id,
            order: targetForm.order as number,
          },
        );
  
        if (res.hasError) {
          showNotification({
            type: "error",
            description: t("Common:internalServerError"),
          });
        } else {
          fetchData();
        }
      } catch (error) {
        if (error instanceof Error) {
          showNotification({
            type: "notification",
            description: t(error.message),
          });
        } else {
          showNotification({
            type: "error",
            description: t("Common:internalServerError"),
          });
        }
      } finally {
        setModalProps({
          isOpen: false,
          type: "swap",
          name: "",
          id: 0,
          loading: false,
        });
      }
    };
  
    const onExportClicked = async () => {
      try {
        const response = await FormTemplatesService.exportToExcel({
          sort: sortState
          ? Object.keys(sortState).map((key) => ({
              field: key,
              dir: sortState[key],
            }))
          : [],
          filter: [
            ...prepareFilters(filters)
          ],
          search: searchValue,
        });
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "evaluationForms.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        showNotification({
          type: "error",
          description: t("Common:internalServerError"),
        });
      }
    };

  const onRowDragEnd = ({ from, to }: { from: number; to: number }) => {
    showNotification({
      description: `نقل من ${from + 1} الى ${to + 1}`,
      type: "notification",
    });
  };

  const gridSchema = useMemo<GridSchema[]>(
    () => [
      {
        field: "order",
        displayName: "#",
        showOnMobile: true,
      },
      {
        field: "templateName",
        displayName: t("templateName"),
        filterable: true,
        showOnMobile: true,
        render : (row:PublishingFormLocalResult) =>
        {
          return <>{row.publishingFormDetail.displayName}</>;
        }
      },
      {
        field: "publicationType",
        displayName: t("publicationType"),
        render : (row:PublishingFormLocalResult) =>
        {
          return <>{row.typeOfScientificDetail.displayName}</>
        }
      },
      {
        field: "publicationCategory",
        displayName: t("publicationCategory"),
        render : (row:PublishingFormLocalResult) =>
          {
            return <>{row.scientificClassificationDetail.displayName}</>
          }
      },
      {
        field: "createdOn",
        displayName: t("createdOn"),
        render : (row:PublishingFormLocalResult) =>
          {
            return <>{toSystemThemeDateFormat(row.createOn)}</>;
          }
      },
      {
        field: "status",
        displayName: t("Common:status"),
        render : (row:PublishingFormLocalResult) =>
          {
            return <>{t(`Common:${row.status}`)}</>;
          }
      },
      {
        field: "lastModifiedBy",
        displayName: t("lastModifiedBy"),
        render : (row:PublishingFormLocalResult) =>
          {
            return `${row.updatedBy} ${row.updatedOn ? `(${toSystemThemeDateFormat(row.updatedOn)})` : ""}`;
          }
      },
      {
        field: "id",
        displayName: t("Common:actions"),
        showOnMobile: true,
        render: (row: PublishingFormLocalResult) => {
          return (
             <>
                <Link to={`/home/formTemplates/form?id=${row.id}`} className="btn text-darkGray btn-sm">
                  <i className="icon-edit" data-bs-toggle="tooltip" data-bs-placement="top" title={t("Common:edit")} />
                </Link>

                <span
                  role="button"
                  onClick={() => {
                    setModalProps({
                      isOpen: true,
                      type: modalProps.type,
                      name: row.publishingFormDetail.displayName,
                      id: row.id,
                      loading: false,
                    });
                  }}
                  className="btn text-danger btn-sm"
                >
                  <i
                    className="icon-delete"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={t("Common:delete")}
                  />
                </span>
             </>
          );
        },
      },
    ],
    [t, toSystemThemeDateFormat],
  );

  const viewHeader: GridViewHeaderProps = {
    title: t("formTemplates"),
    singularName: t("formTemplate"),
    totalCount: totalCount,
    onExportClick: exportToExcel,
    addPageRoute: "/home/formTemplates/form",
  };

  const onDeleteConfirmed = async () => {
    setModalProps({ ...modalProps, loading: true });
    try {
      const res = await FormTemplatesService.delete(modalProps.id);
      if (res.hasError) {
        showNotification({
          type: "error",
          description: t("Common:internalServerError"),
        });
      } else {
        showNotification({
          type: "success",
          description: t("Common:success"),
        });
       await fetchData({ resetPagination: true });
       resetGridStore();
      }
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setModalProps({
        isOpen: false,
        type: "delete",
        name: "",
        id: 0,
        loading: false,
      });
    }
  };

  return (
    <>
    <GridView
      loading={loading}
      gridProps={{
        data: gridData ?? [],
        gridSchema,
        draggable: true,
        onRowDragEnd: onSwapFormOrder,
      }}
      viewHeaderProps={viewHeader}
    />
      <ActionModal
        isOpen={modalProps.isOpen}
        onClose={() => {
          setModalProps({
            isOpen: false,
            type: "delete",
            name: "",
            id: 0,
            loading: false,
          });
        }}
        headerMsg={t("Common:areYouSure")}
        subMsg={t("deleteEvalFormModalSubMsg", {
          name: modalProps.name,
        })}
        actionIcon="icon-delete"
        confirmBtnText={t("Common:delete")}
        iconWrapperColor={"danger"}
        onActionConfirm={onDeleteConfirmed}
        loading={modalProps.loading}
      />
    </>
  );
};

export default FormTemplatesGridView;
